import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Clock, MapPin, AlertCircle, Star, Share2, MessageCircle, Gift } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getAstrologicalReading } from '../../lib/openai';
import { createReadingWithFreeAccess } from '../../lib/supabase';
import Card, { CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { trackEvent, trackException } from '../../lib/analytics';

const readingTypeInfo = {
  daily: {
    title: 'Daily Horoscope',
    description: 'Get insights into your day ahead based on planetary positions',
  },
  weekly: {
    title: 'Weekly Prediction',
    description: 'Understand the celestial influences for the upcoming week',
  },
  monthly: {
    title: 'Monthly Forecast',
    description: 'Discover how the stars will shape your month ahead',
  },
  quarterly: {
    title: 'Quarterly Forecast',
    description: 'Three-month astrological guidance for the season ahead',
  },
  yearly: {
    title: 'Yearly Forecast',
    description: 'Comprehensive astrological guidance for the entire year ahead',
  },
  career: {
    title: 'Career Reading',
    description: 'Astrological insights into your professional path',
  },
  love: {
    title: 'Love & Relationships',
    description: 'Understand your romantic destiny and relationship patterns',
  },
  health: {
    title: 'Health & Wellness',
    description: 'Celestial guidance for your physical and mental wellbeing',
  },
  life: {
    title: 'Life Prediction',
    description: 'Complete life path and destiny insights',
  },
};

const ReadingForm: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [showAccessOptions, setShowAccessOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const readingType = type as keyof typeof readingTypeInfo;
  const info = readingTypeInfo[readingType];

  useEffect(() => {
    // Track page visit
    if (info) {
      trackEvent('reading_form', 'visit', readingType);
    }
  }, [readingType, info]);

  useEffect(() => {
    // If invalid reading type, redirect to home
    if (!info) {
      trackEvent('reading_form', 'error', 'invalid_reading_type');
      navigate('/');
      return;
    }
  }, [info, navigate]);

  if (!info) {
    navigate('/');
    return null;
  }

  const isProfileComplete = () => {
    return userProfile?.name && 
           userProfile?.birth_date && 
           userProfile?.birth_time && 
           userProfile?.birth_location?.city;
  };

  const getAccessType = () => {
    if (!userProfile) return 'none';
    
    if (userProfile.is_premium) return 'premium';
    if (!userProfile.has_used_free_reading) return 'free';
    if (userProfile.shares_available && userProfile.shares_available > 0) return 'share';
    return 'none';
  };

  const canGenerateReading = () => {
    return getAccessType() !== 'none';
  };

  const handleInitialGenerate = () => {
    if (!user || !userProfile) {
      setError('User profile is incomplete. Please update your profile first.');
      trackEvent('reading_generation', 'error', 'incomplete_profile');
      return;
    }

    if (!isProfileComplete()) {
      setError('Please complete your profile with birth details first.');
      trackEvent('reading_generation', 'error', 'incomplete_birth_details');
      return;
    }

    // Check if user already has access
    if (canGenerateReading()) {
      handleActualGeneration();
    } else {
      // Show access options
      setShowAccessOptions(true);
      trackEvent('reading_generation', 'access_gate_shown', readingType);
    }
  };

  const handleActualGeneration = async () => {
    if (!user || !userProfile) {
      setError('User profile is incomplete. Please update your profile first.');
      trackEvent('reading_generation', 'error', 'incomplete_profile');
      return;
    }

    if (!canGenerateReading()) {
      setError('You need to either use your free reading, have shares available, or upgrade to premium.');
      trackEvent('reading_generation', 'blocked', 'no_access');
      return;
    }

    setLoading(true);
    setError('');
    
    const accessType = getAccessType();
    trackEvent('reading_generation', 'attempt', `${readingType}_${accessType}`);

    try {
      // Get the prediction from OpenAI
      const prediction = await getAstrologicalReading({
        readingType: readingType as any,
        userData: {
          name: userProfile.name,
          dob: userProfile.birth_date,
          birthTime: userProfile.birth_time,
          birthPlace: userProfile.birth_location.city,
        },
      });

      // Prepare reading data
      const readingData = {
        user_phone: user.phoneNumber,
        reading_type: readingType,
        prediction_text: prediction,
      };
      
      // Use the new free reading system
      const savedReading = await createReadingWithFreeAccess(readingData);
      trackEvent('reading_generation', 'success', `${readingType}_${accessType}`);

      // Track user engagement
      trackEvent('user_engagement', 'reading_completed', readingType);

      // Refresh user profile to get updated status
      await refreshUserProfile();

      // Navigate to the reading result page
      navigate(`/reading/result/${savedReading.reading_id}`);
    } catch (err: any) {
      console.error('Error getting reading:', err);
      const errorMessage = err.message || 'Failed to generate your reading. Please try again later.';
      setError(errorMessage);
      
      trackEvent('reading_generation', 'error', readingType);
      trackException(`Reading generation failed for ${readingType}: ${errorMessage}`, false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    trackEvent('conversion', 'subscription_from_reading', readingType);
    navigate('/premium');
  };

  const handleShareUnlock = () => {
    trackEvent('conversion', 'share_from_reading', readingType);
    navigate('/share-unlock');
  };

  const handleBackToOptions = () => {
    setShowAccessOptions(false);
    setError('');
  };

  const renderAccessStatus = () => {
    const accessType = getAccessType();
    
    if (accessType === 'premium') {
      return (
        <Card className="border-secondary-200 bg-secondary-50">
          <CardContent className="py-4">
            <div className="flex items-center">
              <Star className="h-6 w-6 text-secondary-500 mr-3" />
              <div>
                <h3 className="text-base font-semibold text-secondary-800">Premium Access Active</h3>
                <p className="text-secondary-700 text-sm">
                  You have unlimited access to all readings as a premium member
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    if (accessType === 'free') {
      return (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-4">
            <div className="flex items-center">
              <Gift className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <h3 className="text-base font-semibold text-green-800">Free Reading Available!</h3>
                <p className="text-green-700 text-sm">
                  Enjoy your complimentary first reading - no payment or sharing required
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    if (accessType === 'share') {
      return (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Share2 className="h-6 w-6 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-base font-semibold text-blue-800">
                    {userProfile?.shares_available} Reading{userProfile?.shares_available === 1 ? '' : 's'} Available
                  </h3>
                  <p className="text-blue-700 text-sm">
                    You have {userProfile?.shares_available} reading{userProfile?.shares_available === 1 ? '' : 's'} from sharing
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => {
            trackEvent('navigation', 'back_from_reading_form', readingType);
            navigate('/');
          }}
          className="mb-6" 
          leftIcon={<ArrowLeft size={16} />}
        >
          Back to Dashboard
        </Button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-800 font-display">{info.title}</h1>
          <p className="mt-2 text-neutral-600">{info.description}</p>
        </div>

        <div className="space-y-6">
          {/* Show access options if user needs to choose */}
          {showAccessOptions && !canGenerateReading() ? (
            <>
              <Card className="border-primary-200 bg-primary-50">
                <CardContent className="py-6">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-primary-800 mb-2">
                      Choose how to unlock your {info.title}
                    </h3>
                    <p className="text-primary-700 text-sm mb-6">
                      Select one of the options below to access your personalized reading
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Button 
                        variant="primary"
                        onClick={handleSubscribe}
                        leftIcon={<Star size={20} />}
                        size="lg"
                        className="py-4"
                      >
                        Get Premium
                      </Button>
                      
                      <Button 
                        variant="outline"
                        onClick={handleShareUnlock}
                        leftIcon={<Share2 size={20} />}
                        size="lg"
                        className="py-4 border-2 border-primary-600 text-primary-700 hover:bg-primary-50"
                      >
                        Share to Unlock
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="py-4">
                  <div className="text-center">
                    <Button 
                      variant="ghost"
                      onClick={handleBackToOptions}
                      size="sm"
                    >
                      ← Back to Reading Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Current Access Status */}
              {renderAccessStatus()}

              {/* Birth Details Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Your Birth Details
                  </CardTitle>
                  <CardDescription>
                    We'll use your birth information to generate a personalized {readingType} reading
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {userProfile ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center text-sm">
                        <User className="h-4 w-4 text-neutral-500 mr-2" />
                        <span className="font-medium mr-2">Name:</span>
                        <span>{userProfile.name || 'Not set'}</span>
                      </div>
                      
                      <div className="flex items-center text-sm">
                        <Calendar className="h-4 w-4 text-neutral-500 mr-2" />
                        <span className="font-medium mr-2">Birth Date:</span>
                        <span>{userProfile.birth_date || 'Not set'}</span>
                      </div>
                      
                      <div className="flex items-center text-sm">
                        <Clock className="h-4 w-4 text-neutral-500 mr-2" />
                        <span className="font-medium mr-2">Birth Time:</span>
                        <span>{userProfile.birth_time || 'Not set'}</span>
                      </div>
                      
                      <div className="flex items-center text-sm">
                        <MapPin className="h-4 w-4 text-neutral-500 mr-2" />
                        <span className="font-medium mr-2">Birth Place:</span>
                        <span>
                          {userProfile.birth_location ? 
                            `${userProfile.birth_location.city}, ${userProfile.birth_location.country}` : 
                            'Not set'
                          }
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-neutral-600">Loading your details...</p>
                  )}
                  
                  {!isProfileComplete() && (
                    <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg">
                      <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-error-600 mr-2" />
                        <div>
                          <p className="text-error-600 text-sm mb-2">Your profile is incomplete. Please complete your birth details first.</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              trackEvent('navigation', 'profile_from_reading', 'incomplete_profile');
                              navigate('/profile');
                            }}
                          >
                            Complete Profile
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Generate Reading Button */}
              <div className="text-center">
                {error && (
                  <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
                    <p className="text-error-600 text-sm">{error}</p>
                  </div>
                )}
                
                <Button 
                  onClick={canGenerateReading() ? handleActualGeneration : handleInitialGenerate}
                  isLoading={loading}
                  size="lg"
                  className="w-full sm:w-auto px-8"
                  disabled={!isProfileComplete()}
                >
                  {loading ? 'Generating Reading...' : 
                   getAccessType() === 'free' ? `Get Your Free ${info.title}` :
                   `Generate ${info.title}`}
                </Button>
                
                {!isProfileComplete() && (
                  <p className="text-sm text-neutral-500 mt-2">
                    Complete your profile to generate readings
                  </p>
                )}
                
                {getAccessType() === 'free' && (
                  <p className="text-sm text-green-600 mt-2 font-medium">
                    🎁 Your first reading is completely free!
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReadingForm;